import React, { useState } from 'react';
import {
  User,
  Mail,
  Phone,
  Calendar,
  CreditCard,
  RefreshCw,
  ExternalLink,
  ChevronDown,
  AlertTriangle,
  CheckCircle,
  Clock,
  X,
} from 'lucide-react';
import {
  TicketDetail,
  TicketStatus,
  TicketPriority,
  Message,
  SupportCall,
  TwilioCallContext,
} from '../../types/support';
import {
  cn,
  formatDateTime,
  formatRelativeTime,
  getStatusLabel,
  getStatusBadgeClass,
  getPriorityLabel,
  getPriorityBadgeClass,
  getCategoryLabel,
  getEscalationLabel,
  getUserDisplayName,
  formatPhoneNumber,
} from '../../utils/helpers';
import Avatar from '../ui/Avatar';
import Badge from '../ui/Badge';
import Spinner from '../ui/Spinner';
import EmptyState from '../ui/EmptyState';
import SupportMessageList from './SupportMessageList';
import SupportMessageComposer from './SupportMessageComposer';
import SupportCallControls from './SupportCallControls';
import SupportCallHistory from './SupportCallHistory';

interface SupportTicketDetailProps {
  ticket: TicketDetail | null;
  loading: boolean;
  messages: Message[];
  messagesLoading: boolean;
  calls: SupportCall[];
  callsLoading: boolean;
  callContext: TwilioCallContext;
  isVoiceReady: boolean;
  isVoiceConfigured: boolean;
  onSendMessage: (content: string, type: 'APP' | 'INTERNAL_NOTE') => Promise<boolean>;
  onUpdateStatus: (status: TicketStatus) => Promise<boolean>;
  onUpdatePriority: (priority: TicketPriority) => Promise<boolean>;
  onEscalate: (reason: string) => Promise<boolean>;
  onMakeCall: () => void;
  onHangup: () => void;
  onToggleMute: () => void;
  onClose: () => void;
}

const SupportTicketDetail: React.FC<SupportTicketDetailProps> = ({
  ticket,
  loading,
  messages,
  messagesLoading,
  calls,
  callsLoading,
  callContext,
  isVoiceReady,
  isVoiceConfigured,
  onSendMessage,
  onUpdateStatus,
  onUpdatePriority,
  onEscalate,
  onMakeCall,
  onHangup,
  onToggleMute,
  onClose,
}) => {
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [escalateReason, setEscalateReason] = useState('');
  const [escalating, setEscalating] = useState(false);
  const [activeTab, setActiveTab] = useState<'messages' | 'calls'>('messages');

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-card">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex-1 flex items-center justify-center bg-card">
        <EmptyState
          title="Select a ticket"
          description="Choose a ticket from the list to view details"
        />
      </div>
    );
  }

  const isEscalated = ticket.escalationStatus !== 'NONE';
  const canResolve =
    ticket.status !== TicketStatus.RESOLVED &&
    ticket.status !== TicketStatus.CLOSED;

  const handleEscalate = async () => {
    if (!escalateReason.trim()) return;
    setEscalating(true);
    const success = await onEscalate(escalateReason.trim());
    if (success) {
      setShowEscalateModal(false);
      setEscalateReason('');
    }
    setEscalating(false);
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-card">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-lg font-bold text-foreground truncate">
                {ticket.title}
              </h2>
              {isEscalated && (
                <Badge variant="error" size="sm">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Escalated
                </Badge>
              )}
            </div>

            {/* Status/Priority badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <StatusDropdown
                currentStatus={ticket.status}
                onChange={onUpdateStatus}
              />
              <PriorityDropdown
                currentPriority={ticket.priority}
                onChange={onUpdatePriority}
              />
              <Badge variant="muted" size="sm">
                {getCategoryLabel(ticket.category)}
              </Badge>
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {canResolve && (
              <>
                <button
                  onClick={() => onUpdateStatus(TicketStatus.RESOLVED)}
                  className="btn btn-outline text-success border-success/30 hover:bg-success/10"
                >
                  <CheckCircle className="h-4 w-4 mr-1.5" />
                  Resolve
                </button>
                {!isEscalated && (
                  <button
                    onClick={() => setShowEscalateModal(true)}
                    className="btn btn-outline text-warning border-warning/30 hover:bg-warning/10"
                  >
                    <AlertTriangle className="h-4 w-4 mr-1.5" />
                    Escalate
                  </button>
                )}
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-primary/10 text-muted-foreground lg:hidden"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Meta info */}
        <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Created {formatRelativeTime(ticket.createdAt)}
          </span>
          <span className="flex items-center gap-1">
            <RefreshCw className="h-3 w-3" />
            Updated {formatRelativeTime(ticket.updatedAt)}
          </span>
        </div>
      </div>

      {/* Main content area - split layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Messages/Calls panel */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Tab switcher */}
          <div className="flex border-b border-border">
            <button
              onClick={() => setActiveTab('messages')}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'messages'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              Messages ({messages.length})
            </button>
            <button
              onClick={() => setActiveTab('calls')}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'calls'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              Calls ({calls.length})
            </button>
          </div>

          {/* Tab content */}
          {activeTab === 'messages' ? (
            <>
              <SupportMessageList messages={messages} loading={messagesLoading} />
              <SupportMessageComposer
                onSend={onSendMessage}
                disabled={
                  ticket.status === TicketStatus.CLOSED ||
                  ticket.status === TicketStatus.RESOLVED
                }
              />
            </>
          ) : (
            <div className="flex-1 overflow-y-auto scrollbar-thin">
              <SupportCallHistory calls={calls} loading={callsLoading} />
            </div>
          )}
        </div>

        {/* Right sidebar - User info & Call controls */}
        <div className="w-80 border-l border-border flex-shrink-0 overflow-y-auto scrollbar-thin hidden lg:block">
          {/* User info */}
          <div className="p-4 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground mb-3">
              Customer Info
            </h3>
            <div className="flex items-center gap-3 mb-4">
              <Avatar
                name={getUserDisplayName(ticket.user, ticket.userId)}
                src={ticket.user?.profileImage}
                size="lg"
              />
              <div>
                <p className="font-medium text-foreground">
                  {getUserDisplayName(ticket.user, ticket.userId)}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-primary">
                    {ticket.user?.userType === 'PARENT' ? 'Parent' : 'Provider'}
                  </span>
                  {ticket.user?.isVerified && (
                    <span className="text-xs text-success flex items-center gap-0.5">
                      <CheckCircle className="h-3 w-3" /> Verified
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-2 mb-4">
              {ticket.user?.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <a
                    href={`mailto:${ticket.user.email}`}
                    className="text-primary hover:underline truncate"
                  >
                    {ticket.user.email}
                  </a>
                </div>
              )}
              {ticket.user?.phoneNumber && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-foreground font-mono">
                    {formatPhoneNumber(ticket.user.phoneNumber)}
                  </span>
                </div>
              )}
            </div>

            {/* Location */}
            {(ticket.user?.city || ticket.user?.state || ticket.user?.country) && (
              <div className="mb-4">
                <p className="text-xs text-muted-foreground mb-1">Location</p>
                <p className="text-sm text-foreground">
                  {[ticket.user?.city, ticket.user?.state, ticket.user?.country]
                    .filter(Boolean)
                    .join(', ')}
                </p>
                {ticket.user?.timezone && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Timezone: {ticket.user.timezone}
                  </p>
                )}
              </div>
            )}

            {/* Bio */}
            {ticket.user?.bio && (
              <div className="mb-4">
                <p className="text-xs text-muted-foreground mb-1">About</p>
                <p className="text-sm text-foreground line-clamp-3">
                  {ticket.user.bio}
                </p>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              {ticket.user?._count && (
                <>
                  <div className="p-2 bg-background rounded-lg text-center">
                    <p className="text-lg font-bold text-foreground">
                      {ticket.user._count.bookingsAsCustomer}
                    </p>
                    <p className="text-xs text-muted-foreground">Bookings</p>
                  </div>
                  {ticket.user.userType === 'PROVIDER' && (
                    <div className="p-2 bg-background rounded-lg text-center">
                      <p className="text-lg font-bold text-foreground">
                        {ticket.user._count.bookingsAsProvider}
                      </p>
                      <p className="text-xs text-muted-foreground">As Provider</p>
                    </div>
                  )}
                </>
              )}
              {ticket.user?.customerRating !== undefined && ticket.user.customerRating > 0 && (
                <div className="p-2 bg-background rounded-lg text-center">
                  <p className="text-lg font-bold text-foreground">
                    {ticket.user.customerRating.toFixed(1)}★
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Rating ({ticket.user.customerRatingCount})
                  </p>
                </div>
              )}
            </div>

            {/* Account Details */}
            <div className="space-y-1.5 text-xs text-muted-foreground border-t border-border pt-3">
              {ticket.user?.createdAt && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-3 w-3" />
                  <span>Member since {formatDateTime(ticket.user.createdAt)}</span>
                </div>
              )}
              {ticket.user?.lastSeenAt && (
                <div className="flex items-center gap-2">
                  <Clock className="h-3 w-3" />
                  <span>Last seen {formatRelativeTime(ticket.user.lastSeenAt)}</span>
                </div>
              )}
              {ticket.user?.platform && (
                <div className="flex items-center gap-2">
                  <User className="h-3 w-3" />
                  <span>Platform: {ticket.user.platform}</span>
                </div>
              )}
              {ticket.user?.householdId && (
                <div className="flex items-center gap-2">
                  <User className="h-3 w-3" />
                  <span>
                    Household member
                    {ticket.user.relationshipToOwner && ` (${ticket.user.relationshipToOwner})`}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Call controls */}
          <div className="p-4 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground mb-3">
              Call Customer
            </h3>
            <SupportCallControls
              phoneNumber={ticket.user?.phoneNumber}
              callContext={callContext}
              isReady={isVoiceReady}
              isConfigured={isVoiceConfigured}
              onMakeCall={onMakeCall}
              onHangup={onHangup}
              onToggleMute={onToggleMute}
            />
          </div>

          {/* Related entities */}
          {(ticket.booking || ticket.payment || ticket.refund) && (
            <div className="p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">
                Related
              </h3>
              <div className="space-y-2">
                {ticket.booking && (
                  <div className="p-3 bg-background rounded-lg border border-border">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-primary" />
                      <span className="font-medium text-foreground">
                        Booking
                      </span>
                      <Badge variant="info" size="sm">
                        {ticket.booking.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDateTime(ticket.booking.scheduledAt)}
                    </p>
                    {ticket.booking.providerName && (
                      <p className="text-xs text-muted-foreground">
                        with {ticket.booking.providerName}
                      </p>
                    )}
                  </div>
                )}
                {ticket.payment && (
                  <div className="p-3 bg-background rounded-lg border border-border">
                    <div className="flex items-center gap-2 text-sm">
                      <CreditCard className="h-4 w-4 text-primary" />
                      <span className="font-medium text-foreground">
                        Payment
                      </span>
                      <Badge
                        variant={
                          ticket.payment.status === 'completed'
                            ? 'success'
                            : 'warning'
                        }
                        size="sm"
                      >
                        {ticket.payment.status}
                      </Badge>
                    </div>
                    <p className="text-sm font-mono text-foreground mt-1">
                      ${(ticket.payment.amount / 100).toFixed(2)}
                    </p>
                  </div>
                )}
                {ticket.refund && (
                  <div className="p-3 bg-background rounded-lg border border-border">
                    <div className="flex items-center gap-2 text-sm">
                      <RefreshCw className="h-4 w-4 text-warning" />
                      <span className="font-medium text-foreground">
                        Refund
                      </span>
                      <Badge
                        variant={
                          ticket.refund.status === 'completed'
                            ? 'success'
                            : 'warning'
                        }
                        size="sm"
                      >
                        {ticket.refund.status}
                      </Badge>
                    </div>
                    <p className="text-sm font-mono text-foreground mt-1">
                      ${(ticket.refund.amount / 100).toFixed(2)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Escalate Modal */}
      {showEscalateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowEscalateModal(false)}
          />
          <div className="relative bg-card border border-border rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl animate-slide-up">
            <h3 className="text-lg font-bold text-foreground mb-2">
              Escalate Ticket
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              This will escalate the ticket to admin review and set priority to
              Urgent.
            </p>
            <textarea
              value={escalateReason}
              onChange={(e) => setEscalateReason(e.target.value)}
              placeholder="Enter reason for escalation..."
              className="textarea h-24 mb-4"
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowEscalateModal(false)}
                className="btn btn-ghost"
              >
                Cancel
              </button>
              <button
                onClick={handleEscalate}
                disabled={!escalateReason.trim() || escalating}
                className="btn bg-warning text-warning-foreground hover:bg-warning/90"
              >
                {escalating ? 'Escalating...' : 'Escalate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Status dropdown component
const StatusDropdown: React.FC<{
  currentStatus: TicketStatus;
  onChange: (status: TicketStatus) => Promise<boolean>;
}> = ({ currentStatus, onChange }) => {
  const [open, setOpen] = useState(false);

  const statuses = [
    TicketStatus.OPEN,
    TicketStatus.IN_PROGRESS,
    TicketStatus.WAITING_FOR_CUSTOMER,
    TicketStatus.RESOLVED,
    TicketStatus.CLOSED,
  ];

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'badge flex items-center gap-1',
          getStatusBadgeClass(currentStatus)
        )}
      >
        {getStatusLabel(currentStatus)}
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
          />
          <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-20 py-1 min-w-[140px]">
            {statuses.map((status) => (
              <button
                key={status}
                onClick={async () => {
                  await onChange(status);
                  setOpen(false);
                }}
                className={cn(
                  'w-full text-left px-3 py-1.5 text-sm hover:bg-primary/10 transition-colors',
                  status === currentStatus
                    ? 'text-primary font-medium'
                    : 'text-foreground'
                )}
              >
                {getStatusLabel(status)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Priority dropdown component
const PriorityDropdown: React.FC<{
  currentPriority: TicketPriority;
  onChange: (priority: TicketPriority) => Promise<boolean>;
}> = ({ currentPriority, onChange }) => {
  const [open, setOpen] = useState(false);

  const priorities = [
    TicketPriority.LOW,
    TicketPriority.MEDIUM,
    TicketPriority.HIGH,
    TicketPriority.URGENT,
  ];

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'badge flex items-center gap-1',
          getPriorityBadgeClass(currentPriority)
        )}
      >
        {getPriorityLabel(currentPriority)}
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
          />
          <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-20 py-1 min-w-[100px]">
            {priorities.map((priority) => (
              <button
                key={priority}
                onClick={async () => {
                  await onChange(priority);
                  setOpen(false);
                }}
                className={cn(
                  'w-full text-left px-3 py-1.5 text-sm hover:bg-primary/10 transition-colors',
                  priority === currentPriority
                    ? 'text-primary font-medium'
                    : 'text-foreground'
                )}
              >
                {getPriorityLabel(priority)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default SupportTicketDetail;
